import { useOutletContext } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { Link } from '@factorialco/factorial-one-react'
import {
  IndicatorsList,
  RichTextDisplay,
  Widget,
} from '@factorialco/factorial-one-react/dist/experimental'
import { AlertCircle, CheckCircle } from '@factorialco/factorial-one-react/icons/app'
import {
  CompetencyFragment,
  LearningPlatformOverviewDocument,
  TrainingDetailFragment,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import i18n from 'modules/core/lib/i18n'

import { OverviewTag } from '../OverviewTag'
import { textDisplay } from './index.vanilla.css'

function ScoreWidget({
  score,
}: {
  score: { type: string; numerator: number; denominator: number }
}) {
  const title = (function () {
    switch (score.type) {
      case 'coursesatisfaction': {
        return i18n.t('trainings.trainings_detail.scores.titles.satisfaction')
      }
      case 'courseeffectiveness': {
        return i18n.t('trainings.trainings_detail.scores.titles.effectiveness')
      }
      default: {
        throw new Error(
          `Unexpected score type. Expected "coursesatisfaction" or "courseeffectiveness", got "${score.type}"`
        )
      }
    }
  })()
  const label = (function () {
    switch (score.denominator) {
      case 5: {
        if (score.numerator >= 4.5) return i18n.t('trainings.trainings_detail.scores.very_high')
        if (score.numerator >= 3.5) return i18n.t('trainings.trainings_detail.scores.high')
        if (score.numerator > 2.5) return i18n.t('trainings.trainings_detail.scores.average')
        if (score.numerator > 1.5) return i18n.t('trainings.trainings_detail.scores.low')
        return i18n.t('trainings.trainings_detail.scores.very_low')
      }
      default: {
        throw new Error(`Unexpected denominator. Expected "5", got "${score.denominator}"`)
      }
    }
  })()
  const icon = (function () {
    switch (score.denominator) {
      case 5: {
        if (score.numerator > 2) return CheckCircle
        return AlertCircle
      }
      default: {
        throw new Error(`Unexpected denominator. Expected "5", got "${score.denominator}"`)
      }
    }
  })()
  const color = (function () {
    switch (score.denominator) {
      case 5: {
        if (score.numerator >= 3.5) return 'text-f1-icon-positive'
        if (score.numerator > 2) return 'text-f1-icon-warning'
        return 'text-f1-icon-critical'
      }
      default: {
        throw new Error(`Unexpected denominator. Expected "5", got "${score.denominator}"`)
      }
    }
  })()
  const scoreFormatter = new Intl.NumberFormat(i18n.locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })
  return (
    <Widget
      fullHeight
      key={score.type}
      header={{
        title,
      }}
    >
      <IndicatorsList
        items={[
          {
            color,
            content: `${scoreFormatter.format(score.numerator)}/${score.denominator}`,
            icon,
            label,
          },
        ]}
      />
    </Widget>
  )
}

const MainContent = ({
  training,
  competencies,
}: {
  training: TrainingDetailFragment
  competencies: CompetencyFragment[] | []
}) => {
  const { description, id: trainingId, validFor, objectives } = training
  const { data } = useQuery(LearningPlatformOverviewDocument, {
    variables: { trainingId },
  })
  const { scores } = useOutletContext<{
    scores: { type: string; numerator: number; denominator: number }[]
  }>()
  const { loading: richTextEditorLoading } = useRichTextEditor()
  const satisfactionScores = scores.filter((score) => score.type === 'coursesatisfaction')
  const efffectivenessScores = scores.filter((score) => score.type === 'courseeffectiveness')

  const learningPlatform = data?.trainings.learningPlatformsConnection.nodes[0]

  if (richTextEditorLoading) return null

  return (
    <Box style={{ flex: '2' }} gap="s24">
      {(satisfactionScores.length > 0 || efffectivenessScores.length > 0) && (
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
          }}
        >
          {satisfactionScores.length > 0 &&
            satisfactionScores.map((score) => <ScoreWidget score={score} key={score.type} />)}
          {efffectivenessScores.length > 0 &&
            efffectivenessScores.map((score) => <ScoreWidget score={score} key={score.type} />)}
        </Box>
      )}
      <Box gap="s8">
        <Text.Normal size="300" weight="semibold">
          {i18n.t('trainings.trainings_revamp.training_detail.overview.competencies')}
        </Text.Normal>
        <Box gap="s8" flexDirection="row" flexWrap="wrap">
          {competencies && competencies.length
            ? competencies.map((competency) => (
                <OverviewTag key={competency.id} tagName={competency.name} />
              ))
            : '-'}
        </Box>
      </Box>
      <Box gap="s8">
        <Text.Normal weight="semibold">
          {i18n.t('trainings.trainings_revamp.training_detail.overview.objectives')}
        </Text.Normal>
        <Box className={textDisplay}>
          <RichTextDisplay content={objectives || ''} />
        </Box>
      </Box>
      <Box gap="s8">
        <Text.Normal weight="semibold">
          {i18n.t('trainings.trainings_revamp.training_detail.overview.description')}
        </Text.Normal>
        <Box className={textDisplay}>
          <RichTextDisplay content={description} />
        </Box>
      </Box>
      {validFor && (
        <Box gap="s8">
          <Text.Normal size="300" weight="semibold">
            {i18n.t('trainings.trainings_revamp.training_detail.overview.course_validity')}
          </Text.Normal>
          <Box>
            <Text.Normal>
              {i18n.tp(
                'trainings.trainings_revamp.new_training.wizard.basic_information.course_validity.year_info',
                { count: validFor }
              )}
            </Text.Normal>
          </Box>
        </Box>
      )}
      {learningPlatform?.link && (
        <Box gap="s8">
          <Text.Normal size="300" weight="semibold">
            {i18n.t('trainings.my_trainings_revamp.training_detail.overview.learning_platform')}
          </Text.Normal>
          <Link href={learningPlatform?.link}>{learningPlatform?.link}</Link>
        </Box>
      )}
    </Box>
  )
}

export default MainContent
